import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { TenantRoles } from 'modules/application/tenant/TenantService';
import { Contexts } from 'modules/core/ContextProcessorService';
import { applicationListStateName } from 'modules/application/list';
import { marketplaceListStateName } from 'modules/application/marketplace/list';
import { blipStoreStateName } from 'modules/blipStore';
import { cdpHome } from 'modules/cdp';
import { blipPaymentsStateName } from 'modules/blipPayments';
import { blipConversationsInsightsStateName } from 'modules/blipConversationsInsights';
import './mainNavbar.scss';
import { BlipStoreFeatures } from 'modules/blipStore/BlipStoreFeatures';
import { CDPFeatures } from 'modules/cdp/CDPFeatures';
import { NavbarFeatures } from '../NavbarFeatures';
import { BlipPaymentsFeatures } from 'modules/blipPayments/BlipPaymentsFeatures';
import { BlipConversationsInsightsFeatures } from 'modules/blipConversationsInsights/BlipConversationsInsightsFeatures';
const BLIP_STORE_OPENED_TRACKING = 'blip-store-header-option-clicked';
const BLIP_PAYMENT_OPENED_TRACKING = 'blip-payment-header-option-clicked';
import MainNavbarReactView, { APPLICATION_PATH, BLIP_CDP_PATH, BLIP_PAYMENTS_PATH, BLIP_CONVERSATIONS_INSIGHTS_PATH, BLIP_INSIGHTS_PATH, BLIP_STORE_PATH, MARKETPLACE_PATH, NAV_BAR_PATHS } from './MainNavbarReactView';
import { blipInsightsStateName } from 'modules/blipInsights';
const MainNavbarReact = ({ ContextProcessorService, TenantService, SegmentService, $translate, $location, $state, user, BLIP_DOMAIN, StudioService }) => {
    const [canAccessBlipProfiler, setCanAccessBlipProfiler] = useState(false);
    const [isOnTenantContext, setIsOnTenantContext] = useState(false);
    const [canAccessBlipStore, setCanAccessBlipStore] = useState(false);
    const [canAccessBlipPayments, setCanAccessBlipPayments] = useState(false);
    const [canAccessBlipConversationsInsights, setCanAccessBlipConversationsInsights] = useState(false);
    const [canAccessBlipInsights, setCanAccessBlipInsights] = useState(false);
    const [canAccessCdp, setCanAccessCdp] = useState(false);
    const [currentTenant, setCurrentTenant] = useState(null);
    const [isOrganizationEnabled, setIsOrganizationEnabled] = useState(false);
    const [isCreateButtonsDisabledOnSharedView, setIsCreateButtonsDisabledOnSharedView] = useState(false);
    const [canCreateChatbot, setCanCreateChatbot] = useState(false);
    const [activePath, setActivePath] = useState($location.path());
    const [initialized, setInitialized] = useState(false);
    useEffect(() => {
        const initialize = async () => {
            await ContextProcessorService.waitForInitialization();
            setIsOnTenantContext(ContextProcessorService.context === Contexts.Tenants);
            setCurrentTenant(ContextProcessorService.tenant);
            await checkFeatures();
            setCanCreateChatbot(await userCanCreateChatbot());
            setInitialized(true);
            initializeActivePath();
        };
        if (!initialized) {
            initialize();
        }
    }, ['user', 'initialized']);
    const initializeActivePath = () => {
        if (NAV_BAR_PATHS.includes($location.path())) {
            setActivePath($location.path());
        }
    };
    const userCanCreateChatbot = async () => {
        if (ContextProcessorService.context === Contexts.Default) {
            if (isOrganizationEnabled && isCreateButtonsDisabledOnSharedView) {
                return false;
            }
            return true;
        }
        const userIdentity = `${encodeURIComponent(user.email)}@${BLIP_DOMAIN}`;
        const tenantId = ContextProcessorService.tenant.id;
        return await TenantService.hasRequiredTenantRole(userIdentity, tenantId, [TenantRoles.Admin, TenantRoles.Member]);
    };
    const onOpenHome = () => {
        if (!currentTenant) {
            return;
        }
        tenantMenuTrack('organization-home-clicked', {
            status: 'success',
            organizationName: currentTenant.name,
            organizationUrl: TenantService.getTenantUrl(currentTenant.id),
        });
    };
    const onOpenPath = (path, stateName) => {
        if ($location.path() === path) {
            return;
        }
        setActivePath(path);
        $state.go(stateName, {});
        window.dispatchEvent(new CustomEvent('mfe-route-change', { detail: path }));
    };
    const onOpenBlipStore = () => {
        tenantMenuTrack(BLIP_STORE_OPENED_TRACKING, { source: 'Home Button' });
        onOpenPath(BLIP_STORE_PATH, blipStoreStateName);
    };
    const onOpenCDP = () => {
        onOpenPath(BLIP_CDP_PATH, cdpHome);
    };
    const onOpenBlipPayments = () => {
        tenantMenuTrack(BLIP_PAYMENT_OPENED_TRACKING, { source: 'Home Button' });
        onOpenPath(BLIP_PAYMENTS_PATH, blipPaymentsStateName);
    };
    const onOpenBlipConversationsInsights = () => {
        onOpenPath(BLIP_CONVERSATIONS_INSIGHTS_PATH, blipConversationsInsightsStateName);
    };
    const onOpenBlipInsights = () => {
        onOpenPath(BLIP_INSIGHTS_PATH, blipInsightsStateName);
    };
    const tenantMenuTrack = (event, props = {}) => {
        const eventProps = Object.keys(props).length !== 0 ? props : currentTenant;
        SegmentService.createTrack(event, { ...eventProps });
    };
    const checkFeatures = async () => {
        const isAdmin = await TenantService.isContractRoleAuthorized(user.identity, [TenantRoles.Admin]);
        setIsOrganizationEnabled(await TenantService.isOrganizationEnabled());
        setIsCreateButtonsDisabledOnSharedView(await NavbarFeatures.isCreateButtonsOnSharedViewDisabled());
        setCanAccessBlipProfiler(await NavbarFeatures.isBlipProfilerEnabled() && isOnTenantContext);
        setCanAccessBlipStore(await BlipStoreFeatures.isBlipStoreEnabled());
        setCanAccessBlipPayments(await BlipPaymentsFeatures.IsBlipPaymentsEnabled());
        const isUserContractRoleAuthorized = await TenantService.isContractRoleAuthorized(user.identity, [TenantRoles.Admin, TenantRoles.Member]);
        const isTenantIdOnBeta = await CDPFeatures.isTenantIdOnBeta(TenantService.getTenantPrefixFromUrl());
        setCanAccessCdp(isUserContractRoleAuthorized && isTenantIdOnBeta);
        const canAccessBlipConversationsInsights = await BlipConversationsInsightsFeatures.IsBlipConversationsInsightsEnabled(StudioService)
            && isAdmin;
        setCanAccessBlipConversationsInsights(canAccessBlipConversationsInsights);
        setCanAccessBlipInsights(await NavbarFeatures.isBlipInsightsEnabled() && isAdmin);
    };
    const handleNavigation = (path, callback) => {
        setActivePath(path);
        callback();
    };
    const handleNavigationAndGoTo = (path, stateName, extraGoParameters = {}) => {
        const callback = () => {
            onOpenHome();
            $state.go(stateName, extraGoParameters);
        };
        handleNavigation(path, callback);
    };
    const onOpenApplicationHome = () => {
        handleNavigationAndGoTo(APPLICATION_PATH, applicationListStateName);
    };
    const onOpenMarketplace = () => {
        handleNavigationAndGoTo(MARKETPLACE_PATH, marketplaceListStateName, { state: { from: 'Home Button' } });
    };
    return (_jsx(MainNavbarReactView, { canAccessBlipPayments: canAccessBlipPayments, canAccessBlipConversationsInsights: canAccessBlipConversationsInsights, canAccessBlipInsights: canAccessBlipInsights, canAccessBlipProfiler: canAccessBlipProfiler, canAccessBlipStore: canAccessBlipStore, canAccessCdp: canAccessCdp, canCreateChatbot: canCreateChatbot, "$translate": $translate, onOpenApplicationHome: onOpenApplicationHome, onOpenMarketplace: onOpenMarketplace, onOpenBlipStore: onOpenBlipStore, onOpenCDP: onOpenCDP, onOpenBlipPayments: onOpenBlipPayments, onOpenBlipConversationsInsights: onOpenBlipConversationsInsights, onOpenBlipInsights: onOpenBlipInsights, activePath: activePath, isOnTenantContext: isOnTenantContext }));
};
export default MainNavbarReact;
